/* eslint-disable react-hooks/immutability -- this widget updates the existing report scope object directly by design. */
import {useEffect, useReducer, useState} from "react";
import {useAppStore} from "../../../StateManager";
import type {Portfolio} from "../../../data/models/Portfolio";
import type {IPortfolioListScopeItem, Report} from "../../../data/models/Report";
import {XyiconFeature} from "../../../generated/api/base";
import {ReportScope} from "../../../generated/api/reports";
import {SelectInputV5} from "../input/select/SelectInputV5";
import {ReactUtils} from "../../utils/ReactUtils";
import {FieldV5} from "./FieldV5";
import {MultiSelectInputV5} from "./datatypes/multi/MultiSelectInputV5";
import {FieldInputStyled} from "./datatypes/FieldInputV5";

interface IScopeOption {
	id: ReportScope;
	label: string;
}

const _scopeOptions: IScopeOption[] = [
	{
		id: ReportScope.PortfolioIDList,
		label: "Portfolio list",
	},
	{
		id: ReportScope.Organization,
		label: "Organization",
	},
];

interface IReportScopeWidgetProps {
	readonly report: Report;
	readonly onChange?: () => void;
	readonly disabled?: boolean;
	readonly isDetailsPanel?: boolean;
}

export const ReportScopeWidgetV5 = (props: IReportScopeWidgetProps) => {
	const appState = useAppStore((state) => state.appState);
	const {report, onChange, disabled = false, isDetailsPanel = false} = props;
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	// Holds the full list of portfolio names of a Portfolio List scoped report
	const [fullPortfolioNamesList, setFullPortfolioNamesList] = useState<IPortfolioListScopeItem[]>([]);

	const onChangeScope = (option: IScopeOption) => {
		report.scope = option.id;
		report.portfolioIDList = [];
		onChange?.();
		forceUpdate();
	};

	const onChangePortfolioIDList = (value: Portfolio[] | IPortfolioListScopeItem[]) => {
		report.portfolioIDList = value.map((p) => p.id);
		onChange?.();
		forceUpdate();
	};

	const getSelectedOptions = () => {
		return appState.actions.getList(XyiconFeature.Portfolio).filter((option) => report.portfolioIDList.includes(option.id)) as Portfolio[];
	};

	const selectedScope = _scopeOptions.find((option) => option.id === report?.scope);

	const finalDisabled = disabled || report?.reportFeature === XyiconFeature.XyiconCatalog;

	useEffect(() => {
		const updatePortfolioListScope = async () => {
			const portfoliosList = await report.getPortfoliosListScopeByIDsInDefinition();
			setFullPortfolioNamesList(portfoliosList);
		};

		if (isDetailsPanel) {
			updatePortfolioListScope();
		}
	}, [report, report.portfolioIDList, isDetailsPanel]);

	return (
		<>
			<FieldV5 label="Scope">
				{report.scope === ReportScope.CurrentPortfolio && report.definition !== undefined ? (
					"Current Portfolio"
				) : disabled ? (
					selectedScope.label
				) : (
					<SelectInputV5
						sort={false}
						options={_scopeOptions}
						renderOptionWhenClosed={(option) => option.label}
						selected={selectedScope}
						onChange={onChangeScope}
						disabled={finalDisabled}
						isSameWidth={true}
						// verticalAlignment={VerticalAlignment.bottomOuter}
					/>
				)}
			</FieldV5>
			{report?.scope === ReportScope.CurrentPortfolio && (
				<FieldV5
					label="Portfolio"
					className="text currentPortfolio"
				>
					{appState.actions.getCurrentPortfolioName()}
				</FieldV5>
			)}
			{report?.scope === ReportScope.Organization && (
				<FieldV5
					label="Organization"
					className="text"
				>
					{appState.actions.getCurrentOrganizationName()}
				</FieldV5>
			)}
			{report?.scope === ReportScope.PortfolioIDList && (
				<FieldInputStyled className={ReactUtils.cls({disabled: finalDisabled})}>
					<FieldV5
						label="List"
						className="report-scope-portfolio-list"
					>
						{isDetailsPanel ? (
							<MultiSelectInputV5
								options={[] as IPortfolioListScopeItem[]}
								renderOptionWhenClosed={(option) => option.name}
								selected={fullPortfolioNamesList}
								disabled={finalDisabled}
							/>
						) : (
							<MultiSelectInputV5
								options={appState.actions.getList(XyiconFeature.Portfolio) as Portfolio[]}
								renderOptionWhenClosed={(option) => option.name}
								selected={getSelectedOptions()}
								onChange={onChangePortfolioIDList}
								disabled={finalDisabled}
								isSameWidth={true}
							/>
						)}
					</FieldV5>
				</FieldInputStyled>
			)}
		</>
	);
};
